import React, { useEffect, useRef, useState } from "react";
import { CalendarClock, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import Cta from "@/components/ui-kit/Cta";
import { submitReminder } from "@/lib/api";
import { trackFormSubmit } from "@/lib/analytics";

const SESSION_KEY = "etap_exit_shown";

// Desktop only, once per session, dismissible. Focus trap + ESC handled by Dialog.
export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");
  const honeypot = useRef("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px) and (pointer: fine)").matches;
    if (!isDesktop) return undefined;
    if (sessionStorage.getItem(SESSION_KEY)) return undefined;

    const onLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(SESSION_KEY)) {
        sessionStorage.setItem(SESSION_KEY, "1");
        setOpen(true);
      }
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError("Enter a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await submitReminder({ email: value, honeypot: honeypot.current });
      trackFormSubmit("exit-intent-reminder");
      setStatus("done");
    } catch {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="exit-intent-modal">
        {status === "done" ? (
          <div className="py-4 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-etap-mint-200 text-etap-deep-700">
              <CheckCircle2 className="h-7 w-7" aria-hidden="true" />
            </span>
            <h2 className="mt-4 font-display text-2xl font-bold text-etap-deep-800">You are on the list</h2>
            <p className="mt-2 text-sm text-etap-deep-600">
              We will send you the quarterly tax deadline reminders. No spam.
            </p>
            <div className="mt-6">
              <Cta onClick={() => setOpen(false)} variant="primary" testId="exit-intent-close">
                Close
              </Cta>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-etap-primary-50 text-etap-primary">
                <CalendarClock className="h-6 w-6" aria-hidden="true" />
              </span>
              <DialogTitle className="mt-3 font-display text-2xl font-bold text-etap-deep-800">
                Not ready yet?
              </DialogTitle>
              <DialogDescription className="text-etap-deep-600">
                Get the quarterly tax deadline reminder list. One email each quarter so a deadline never surprises you.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-3 space-y-3" noValidate>
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                onChange={(e) => {
                  honeypot.current = e.target.value;
                }}
              />
              <div>
                <label htmlFor="exit-email" className="sr-only">
                  Email address
                </label>
                <Input
                  id="exit-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  maxLength={140}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-etap-deep-100"
                  data-testid="exit-intent-email"
                />
                {error && (
                  <p className="mt-2 text-sm text-red-600" data-testid="exit-intent-error">
                    {error}
                  </p>
                )}
              </div>
              <Cta
                onClick={submit}
                variant="primary"
                size="lg"
                className="w-full"
                testId="exit-intent-submit"
              >
                {status === "loading" ? "Sending..." : "Send me the reminders"}
              </Cta>
              <p className="text-center text-xs text-etap-deep-400">We will not share your email.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
